import type { ReactNode } from "react";
import { MarketSuggestion } from "./MarketSuggestion";
import { MarketComingSoon } from "./MarketComingSoon";
import { HeaderV3 } from "./Header";
import { FooterV3 } from "./Footer";
import { ChatDock } from "./chat/ChatDock";
import { useLocale } from "@/i18n/LocaleProvider";
import { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useIsMobile } from "./useV3";
import { useMarket } from "./MarketProvider";
import { splitMarketPath } from "./markets";

/* The chrome every rebuilt screen renders inside.
 *
 * `.sq-v3` IS THE SWITCH. src/styles/design-components.css is the design's
 * components.css with every selector prefixed by it, because the design's
 * file and src/styles/components.css share 28 class names -- .sq-header,
 * .sq-hero, .sq-logo, .sq-input, .sq-footer among them. A screen that has
 * been rebuilt renders inside this wrapper and gets the design; a screen that
 * has not is untouched by it. When the last screen is migrated the wrapper
 * comes out and the stylesheet stops being scoped.
 *
 * So the rebuild can be shipped a screen at a time without the half-migrated
 * site being a collision of two design systems.
 */
/* ONE CANONICAL URL PER PAGE, which the market prefix would otherwise break.
 *
 * `/` and `/eg/` are the same page. `useDocumentMeta` canonicalises every
 * route to its own pathname, so both would declare themselves canonical and a
 * crawler would see two copies of every screen in the default market. This
 * strips the prefix when it names the DEFAULT market -- the first active one,
 * which is what an unprefixed URL already resolves to -- and leaves every
 * other market's prefix alone, because /sa/ genuinely is a different page.
 *
 * It runs in the SHELL, so it lands after the page's own meta effect: React
 * runs a child's effects before its parent's, and the last writer wins.
 */
/* KAR-132 — /sa/anything, when Saudi Arabia is hidden, becomes /anything.
 *
 * THE PATH IS KEPT. A visitor who asked for /sa/search?for=Rent gets
 * /search?for=Rent, not the home page: they were looking for something, and
 * the market they used to look is the only part that is no longer on offer.
 *
 * `replace`, so the dead URL does not sit in the back button waiting to bounce
 * them again.
 *
 * NOT A 302. A server redirect would have to live in _redirects, which is a
 * static file — and then reopening the market would mean an edit and a deploy
 * rather than the one switch in Admin that this feature promises. The trade is
 * written up in docs/DEVIATIONS.md; say the word and the static rule goes in
 * beside this.
 */
function useHiddenMarketRedirect() {
  const { hiddenMarket, markets } = useMarket();
  const { pathname, search, hash } = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (!hiddenMarket) return;
    const { rest } = splitMarketPath(pathname);
    const open = markets.find((m) => m.active) ?? markets[0];
    /* The default market answers the unprefixed path, so `rest` alone is the
     * equivalent. With no visible market at all there is nowhere to send
     * anyone, and staying put beats a redirect loop. */
    if (!open) return;
    navigate(rest + search + hash, { replace: true });
  }, [hiddenMarket, pathname, search, hash, markets, navigate]);
}

function useMarketCanonical() {
  const { pathname } = useLocation();
  const { markets } = useMarket();

  useEffect(() => {
    const { slug, rest } = splitMarketPath(pathname);
    const isDefault = !!slug && markets.length > 0 && markets[0].slug === slug;
    const path = isDefault ? rest : pathname;
    let link = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (!link) {
      link = document.createElement("link");
      link.setAttribute("rel", "canonical");
      document.head.appendChild(link);
    }
    link.setAttribute("href", window.location.origin + path);
  }, [pathname, markets]);
}

export function ShellV3({ children }: { children: ReactNode }) {
  /* The skip link is not a design string -- it is the one the old shell
     already carried, and it is right. */
  const { t } = useLocale();
  /* `sq-viewport--mobile` IS THE DESIGN'S OWN MOBILE SWITCH, not ours: the
     390 files carry it on <body>, and for several screens -- results above
     all -- it is the ONLY mobile rule. components.css has no
     `@media (max-width:767px)` for .sq-results; it has
     `.sq-viewport--mobile .sq-results`. Without this class those screens
     would render their desktop grid on a phone. */
  const mobile = useIsMobile();
  useHiddenMarketRedirect();
  useMarketCanonical();
  return (
    <div className={mobile ? "sq-v3 sq-viewport--mobile" : "sq-v3"}>
      <a className="sq-skip-link" href="#main">{t.nav.skipToContent}</a>
      <MarketComingSoon />
      <MarketSuggestion />
      <HeaderV3 />
      <main id="main">{children}</main>
      <FooterV3 />
      {/* The dock rides the shell, not a page: a message can be answered from
          wherever the visitor is. It hides itself for guests and on the inbox,
          where the same threads are already on screen. */}
      <ChatDock />
    </div>
  );
}
